import { CampaignStrategyBudgetInfo } from './budget';
import { BannerModerationTheme, CampaignType } from './domain';
import {
    CurrencyCode,
    GdiRecommendationType,
    GdRecommendationSign,
    GdRecommendationStatus
} from './graphql-grid';
import { SortDirection } from './grid';

export interface RecommendationsItemsScreenUrlParams {
    sortKey?: string | null;
    sortDirection?: SortDirection | null;
    campaignsIds?: Array<string>;
}

export enum RecommendationsItemsScreenUrlSearchParams {
    sortKey = 'sort-key',
    sortDirection = 'sort-direction'
}

export enum RecommendationsItemsScreenUrlPathParams {
    ulogin = 'ulogin'
}

export enum RecommendationType {
    dailyBudget = 'dailyBudget',
    removePagesFromBlackList = 'removePagesFromBlackList',
    sendLicenses = 'sendLicenses',
    changeAdGroupWithLowStat = 'changeAdGroupWithLowStat',
    addMorePicturesToAdgroup = 'addMorePicturesToAdgroup',
    changeAdGroupForModeration = 'changeAdGroupForModeration',
    switchOnAutotargeting = 'switchOnAutotargeting',
    changeBannerForModeration = 'changeBannerForModeration',
    addBannerDisplayHrefs = 'addBannerDisplayHrefs',
    addTitleExtension = 'addTitleExtension',
    addSiteLinks = 'addSiteLinks',
    addAdditionsItemCallouts = 'addAdditionsItemCallouts',
    addImageToBanner = 'addImageToBanner',
    addBannerFormats = 'addBannerFormats',
    chooseAppropriatePlacementsForAdGroup = 'chooseAppropriatePlacementsForAdGroup',
    uploadAppropriateCreatives = 'uploadAppropriateCreatives',
    chooseAppropriatePlacementsForBanner = 'chooseAppropriatePlacementsForBanner'
}

export enum RecommendationCharacter {
    warning = 'warning',
    info = 'info',
    attention = 'attention'
}

export enum RecommendationChartDataAxisYType {
    clicks = 'clicks',
    ctr = 'ctr'
}

export enum RecommendationChartDataAxisXType {
    date = 'date'
}

export type AxisYParams = {
    type: RecommendationChartDataAxisYType;
};

export type RecommendationChartDataAxisXDateParams = {
    type: RecommendationChartDataAxisXType.date;
    values: Array<RecommendationChartDataAxisXDateValue>;
};

export type RecommendationChartDataAxisXParams = RecommendationChartDataAxisXDateParams;

export interface RecommendationChartDataAxisXDateValue {
    point: string;
    holiday: boolean;
}

export enum RecommendationsChartViewType {
    flatCurrent = 'flatCurrent',
    normal = 'normal'
}

export interface RecommendationChartData {
    profitValues: Array<number>;
    currentValues: Array<number>;
    xAxis: RecommendationChartDataAxisXParams;
    yAxis: AxisYParams;
    view?: RecommendationsChartViewType;
}

export interface Kpi {
    currency: CurrencyCode;
    periodValue: number;
    clicks: number;
    cost: number;
    sign?: GdRecommendationSign;
    graph?: RecommendationChartData;
    recommendedDailyBudget: number;
    formats?: Array<string>;
    videoFormats?: Array<OutdoorVideoFormat>;
}

export interface RecommendationKey {
    userKey1: string;
    userKey2: string;
    userKey3: string;
    pid: string;
    bid: string;
    cid: string;
    timestamp: string;
    type: GdiRecommendationType;
}

interface BaseRecommendationProps {
    status: GdRecommendationStatus;
    character: RecommendationCharacter;
    keys: Array<RecommendationKey>;
    isApplicable: boolean;
}

interface SimpleRecommendation extends BaseRecommendationProps {
    type:
        | RecommendationType.changeAdGroupWithLowStat
        | RecommendationType.changeAdGroupForModeration
        | RecommendationType.changeBannerForModeration
        | RecommendationType.addBannerDisplayHrefs
        | RecommendationType.addTitleExtension
        | RecommendationType.addSiteLinks
        | RecommendationType.addAdditionsItemCallouts
        | RecommendationType.addImageToBanner;
}

interface RecommendationWithKpi extends BaseRecommendationProps {
    type:
        | RecommendationType.dailyBudget
        | RecommendationType.switchOnAutotargeting
        | RecommendationType.removePagesFromBlackList
        | RecommendationType.addBannerFormats
        | RecommendationType.chooseAppropriatePlacementsForAdGroup
        | RecommendationType.uploadAppropriateCreatives
        | RecommendationType.chooseAppropriatePlacementsForBanner;
    kpi: Kpi;
}

interface LicenseRecommendation extends BaseRecommendationProps {
    type: RecommendationType.sendLicenses;
    theme: BannerModerationTheme;
}

export type Recommendation =
    | RecommendationWithKpi
    | SimpleRecommendation
    | LicenseRecommendation;

export type GridRecommendationsData = {
    data?: Array<ContextRecommendation>;
    recommendationsIconType: RecommendationsIconType;
};

export type ContextRecommendation = Recommendation & {
    items?: Array<string>;
    recommendedDailyBudget?: number;
};

export enum RecommendationsIconType {
    red = 'red',
    yellow = 'yellow'
}

export type RecommendationsGridSummaryRecommendation = Recommendation & {
    totalCount: number;
};

export interface SummaryRecommendationItem {
    name: string;
    clicks: number;
    cost: number;
}

interface SummaryRecommendationSimpleItems {
    totalCount: number;
    campaignsIds: Array<string>;
}

interface SummaryRecommendationItemsWithData
    extends SummaryRecommendationSimpleItems {
    data: Array<SummaryRecommendationItem>;
}

interface SummarySimpleRecommendation extends SimpleRecommendation {
    items: SummaryRecommendationSimpleItems;
}

interface SummaryRecommendationWithKpi extends RecommendationWithKpi {
    items: SummaryRecommendationItemsWithData;
    recommendedDailyBudget?: number;
}

interface SummaryLicenseRecommendation extends LicenseRecommendation {
    items: SummaryRecommendationSimpleItems;
}

export type SummaryRecommendation =
    | SummarySimpleRecommendation
    | SummaryRecommendationWithKpi
    | SummaryLicenseRecommendation;

export interface DailyBudgetRecommendationsItem {
    campaignType: CampaignType;
    name: string;
    id: string;
    currency: CurrencyCode;
    budget: CampaignStrategyBudgetInfo;
    budgetRecommendation: number;
    status: GdRecommendationStatus;
    budgetRecommendationKeys: Array<RecommendationKey>;
    isApplicable: boolean;
    clicks?: number;
    clicksForecast?: number;
    cost?: number;
    costForecast?: number;
    currentDailyBudget?: number;
}

export interface RemovePagesFromBlackListRecommendationsItem {
    id: string;
    name: string;
    currency: CurrencyCode;
    clicksForecast?: number;
    costForecast?: number;
    recommendationKeys: Array<RecommendationKey>;
    status: GdRecommendationStatus;
    isApplicable: boolean;
}

export interface SwitchOnAutotargetingRecommendationsItem {
    id: string;
    name: string;
    recommendationKeys: Array<RecommendationKey>;
    status: GdRecommendationStatus;
    isApplicable: boolean;
    clicks?: number;
    clicksForecast?: number;
    cost?: number;
    costForecast?: number;
    campaign: {
        id: string;
        name: string;
    };
}

export type RecommendationsGridItem =
    | DailyBudgetRecommendationsItem
    | RemovePagesFromBlackListRecommendationsItem
    | SwitchOnAutotargetingRecommendationsItem;

export interface RecommendationsItemsScreenActions {
    submitRecommendation: (
        type: RecommendationType,
        keys: Array<RecommendationKey>,
        id: string,
        name: string
    ) => void;
}

export interface EditPageRecommendationsData {
    isRecommendationsVisible: boolean;
    recommendations: Array<Recommendation>;
}

export interface RecommendationsAgregator {
    isRecommendationsVisible: boolean;
    character?: RecommendationCharacter;
    title?: string;
    recommendation?: Recommendation;
}

export interface RecommendationsModalState {
    isVisible: boolean;
    data: Array<RecommendationModalType>;
    headerHint: string;
}

export interface RecommendationModalType {
    recommendation: Recommendation;
    title?: string;
    titleHint?: string;
}

export interface OutdoorVideoFormat {
    width: number;
    height: number;
    duration: number;
}
